import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { getAuditLog, clearAuditLog } from '../../services/aiAuditLog';
import { useTenant } from '../../context/TenantContext';
import {
  submitErasureRequest,
  listErasureRequests,
  ERASURE_STATUS,
} from '../../services/dataRetentionStore';

/**
 * Settings, account preferences, language, notifications, privacy.
 *
 * Three audiences: employee, admin, auditor. Each sees a relevant view.
 *
 * GDPR-anchored: privacy section gives the user genuine control over their
 * data, language, AI audit log, and (for employees) the parsed CV profile.
 * This is what worker-facing data rights look like in practice (Art. 7
 * info rights + GDPR Arts. 15-17).
 */

const SECTION_IDS = {
  account:       'account',
  preferences:   'preferences',
  notifications: 'notifications',
  privacy:       'privacy',
};

const Settings = () => {
  const { t, i18n } = useTranslation();

  // Read user from storage to drive the account display
  const [user, setUser] = useState(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      setUser(raw ? JSON.parse(raw) : null);
    } catch {}
  }, []);

  const role = user?.role || 'employee';
  const isEmployee = role === 'employee';

  // Profile photo state, persisted to localStorage per user
  const photoKey = user?.email ? `clearequal.profile.photo.${user.email}` : null;
  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState('');
  useEffect(() => {
    if (!photoKey) return;
    try {
      const stored = localStorage.getItem(photoKey);
      if (stored) setPhoto(stored);
    } catch {}
  }, [photoKey]);

  const handlePhotoUpload = (e) => {
    setPhotoError('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setPhotoError(t('settings.account.photoInvalidType'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setPhotoError(t('settings.account.photoTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl !== 'string') return;
      try {
        if (photoKey) {
          localStorage.setItem(photoKey, dataUrl);
          setPhoto(dataUrl);
          // Notify Header in same tab to re-render avatar
          window.dispatchEvent(new Event('clearequal:photo-changed'));
        }
      } catch (err) {
        setPhotoError(t('settings.account.photoSaveError'));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePhotoRemove = () => {
    if (!photoKey) return;
    try {
      localStorage.removeItem(photoKey);
      setPhoto(null);
      setPhotoError('');
      window.dispatchEvent(new Event('clearequal:photo-changed'));
    } catch {}
  };

  // Notification preferences, persisted to localStorage
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem('clearequal.notifPrefs');
      return raw ? JSON.parse(raw) : {
        emailUpdates: true,
        payBandChanges: true,
        complianceAlerts: true,
        productNews: false,
      };
    } catch {
      return { emailUpdates: true, payBandChanges: true, complianceAlerts: true, productNews: false };
    }
  });

  const togglePref = (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { localStorage.setItem('clearequal.notifPrefs', JSON.stringify(next)); } catch {}
  };

  // Audit log preview for privacy section
  const [recentAuditCount, setRecentAuditCount] = useState(0);
  useEffect(() => {
    setRecentAuditCount(getAuditLog({ limit: 500 }).length);
  }, []);

  const handleClearAudit = () => {
    if (window.confirm(t('settings.privacy.clearAuditConfirm'))) {
      clearAuditLog();
      setRecentAuditCount(0);
    }
  };

  const handleClearProfile = () => {
    if (window.confirm(t('settings.privacy.clearProfileConfirm'))) {
      try {
        // Find any clearequal.profile.* keys and remove them
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('clearequal.profile.')) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        alert(t('settings.privacy.clearProfileSuccess'));
      } catch {}
    }
  };

  // ─── Right-to-erasure (Art. 17) ────────────────────────────────────────
  const { tenantId } = useTenant();
  const [erasureRequests, setErasureRequests] = useState([]);
  const [showErasureForm, setShowErasureForm] = useState(false);
  const [erasureReason, setErasureReason] = useState('');
  const [erasureScope, setErasureScope] = useState('all'); // 'all' | 'cv_profile' | 'info_requests'

  useEffect(() => {
    if (user?.email) {
      setErasureRequests(listErasureRequests({ subjectEmail: user.email, tenantId }));
    }
  }, [user, tenantId]);

  const handleSubmitErasure = () => {
    if (!user?.email) return;
    if (!window.confirm(
      'Submit a right-to-erasure request?\n\n' +
      'Your administrator will review and either:\n' +
      '• Approve and delete your data, or\n' +
      '• Refuse, with a documented reason (e.g. legal hold).\n\n' +
      'You will be notified within 30 days under GDPR Art. 12(3).'
    )) return;

    const req = submitErasureRequest({
      subjectEmail: user.email,
      subjectName: user.name,
      submittedBy: user.email,
      reason: erasureReason,
      scope: erasureScope === 'all' ? ['all'] : [erasureScope],
      tenantId,
    });
    setErasureRequests([req, ...erasureRequests]);
    setShowErasureForm(false);
    setErasureReason('');
    alert('Request submitted. You will receive a response within 30 days.');
  };

  const hasPendingErasure = erasureRequests.some((r) => r.status === ERASURE_STATUS.PENDING);

  const handleExportData = () => {
    // GDPR Art. 20, data portability. Bundle everything the app stores
    // about this user from localStorage and download as JSON.
    try {
      const exportObj = {
        exportedAt: new Date().toISOString(),
        user,
        notificationPreferences: prefs,
        language: i18n.language,
        aiAuditLog: getAuditLog({ limit: 1000 }),
        profileData: (() => {
          const profiles = {};
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k.startsWith('clearequal.profile.')) {
              try { profiles[k] = JSON.parse(localStorage.getItem(k)); } catch {}
            }
          }
          return profiles;
        })(),
      };
      const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clearequal-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const breadcrumbItems = [
    { label: t('header.home'), path: '/' },
    { label: t('settings.title'), path: '/settings' },
  ];

  const SectionHeader = ({ id, icon, title, body }) => (
    <div id={id} className="mb-4">
      <div className="flex items-center gap-2.5 mb-1">
        <Icon name={icon} size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      </div>
      {body && <p className="text-sm text-muted-foreground">{body}</p>}
    </div>
  );

  const Toggle = ({ enabled, onChange, label, body }) => (
    <button
      type="button"
      onClick={onChange}
      className="w-full flex items-start justify-between gap-4 py-3 text-left group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {body && <p className="text-xs text-muted-foreground mt-0.5">{body}</p>}
      </div>
      <span
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 mt-0.5 ${
          enabled ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </span>
    </button>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page title */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              {t('settings.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('settings.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8">
            {/* Side nav */}
            <nav className="hidden lg:block sticky top-24 self-start">
              <ul className="space-y-1 text-sm">
                {[
                  { id: SECTION_IDS.account,       label: t('settings.account.title'),       icon: 'User' },
                  { id: SECTION_IDS.preferences,   label: t('settings.preferences.title'),   icon: 'Sliders' },
                  { id: SECTION_IDS.notifications, label: t('settings.notifications.title'), icon: 'Bell' },
                  { id: SECTION_IDS.privacy,       label: t('settings.privacy.title'),       icon: 'Lock' },
                ].map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name={s.icon} size={14} />
                      <span>{s.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="space-y-8">
              {/* Account */}
              <section className="bg-card border border-border rounded-lg p-6 shadow-soft">
                <SectionHeader
                  id={SECTION_IDS.account}
                  icon="User"
                  title={t('settings.account.title')}
                  body={t('settings.account.subtitle')}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('settings.account.name')}</p>
                    <p className="text-sm font-medium text-foreground mt-0.5">{user?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('settings.account.email')}</p>
                    <p className="text-sm font-medium text-foreground mt-0.5">{user?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('settings.account.role')}</p>
                    <p className="text-sm font-medium text-foreground mt-0.5 capitalize">{role}</p>
                  </div>
                  {isEmployee && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('settings.account.profileLink')}</p>
                      <Link
                        to="/employee-portal?tab=profile"
                        className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
                      >
                        <Icon name="UserCircle" size={14} />
                        {t('settings.account.openProfile')}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Profile Photo */}
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-3">{t('settings.account.photoTitle')}</p>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
                      {photo ? (
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Icon name="User" size={28} className="text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-2">{t('settings.account.photoHint')}</p>
                      <div className="flex flex-wrap gap-2">
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium cursor-pointer hover:bg-primary/90 transition-smooth">
                          <Icon name="Upload" size={14} />
                          <span>{t('settings.account.uploadPhoto')}</span>
                          <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                        </label>
                        {photo && (
                          <button
                            type="button"
                            onClick={handlePhotoRemove}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-foreground text-xs font-medium hover:bg-muted transition-smooth"
                          >
                            <Icon name="Trash2" size={14} />
                            <span>{t('settings.account.removePhoto')}</span>
                          </button>
                        )}
                      </div>
                      {photoError && (
                        <p className="text-xs text-destructive mt-2">{photoError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Preferences (language) */}
              <section className="bg-card border border-border rounded-lg p-6 shadow-soft">
                <SectionHeader
                  id={SECTION_IDS.preferences}
                  icon="Sliders"
                  title={t('settings.preferences.title')}
                  body={t('settings.preferences.subtitle')}
                />
                <div className="mt-4">
                  <p className="text-xs text-muted-foreground mb-2">{t('settings.preferences.language')}</p>
                  <div className="flex flex-wrap gap-2">
                    {SUPPORTED_LANGUAGES.map((l) => {
                      const active = i18n.language?.split('-')[0] === l.code;
                      return (
                        <button
                          key={l.code}
                          onClick={() => i18n.changeLanguage(l.code)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm border transition-smooth ${
                            active
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          <span className="text-base leading-none">{l.flag}</span>
                          <span>{l.nativeLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* Notifications */}
              <section className="bg-card border border-border rounded-lg p-6 shadow-soft">
                <SectionHeader
                  id={SECTION_IDS.notifications}
                  icon="Bell"
                  title={t('settings.notifications.title')}
                  body={t('settings.notifications.subtitle')}
                />
                <div className="divide-y divide-border mt-2">
                  <Toggle
                    enabled={prefs.emailUpdates}
                    onChange={() => togglePref('emailUpdates')}
                    label={t('settings.notifications.emailUpdates')}
                    body={t('settings.notifications.emailUpdatesBody')}
                  />
                  <Toggle
                    enabled={prefs.payBandChanges}
                    onChange={() => togglePref('payBandChanges')}
                    label={t('settings.notifications.payBandChanges')}
                    body={t('settings.notifications.payBandChangesBody')}
                  />
                  <Toggle
                    enabled={prefs.complianceAlerts}
                    onChange={() => togglePref('complianceAlerts')}
                    label={t('settings.notifications.complianceAlerts')}
                    body={t('settings.notifications.complianceAlertsBody')}
                  />
                  <Toggle
                    enabled={prefs.productNews}
                    onChange={() => togglePref('productNews')}
                    label={t('settings.notifications.productNews')}
                    body={t('settings.notifications.productNewsBody')}
                  />
                </div>
              </section>

              {/* Privacy / GDPR */}
              <section className="bg-card border border-border rounded-lg p-6 shadow-soft">
                <SectionHeader
                  id={SECTION_IDS.privacy}
                  icon="Lock"
                  title={t('settings.privacy.title')}
                  body={t('settings.privacy.subtitle')}
                />
                <div className="space-y-4 mt-4">
                  {/* Right to erasure, Art. 17 */}
                  <div className="p-4 border border-destructive/30 bg-destructive/5 rounded-md">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                          <Icon name="UserX" size={14} className="text-destructive" />
                          Right to be forgotten (GDPR Art. 17)
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          Submit a request to permanently delete your personal data. Your administrator
                          will review within 30 days. Some data (tax records, anonymised statistics)
                          may be retained for legal reasons, they will explain if so.
                        </p>
                      </div>
                      {!showErasureForm && !hasPendingErasure && (
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="UserX"
                          onClick={() => setShowErasureForm(true)}
                        >
                          Request deletion
                        </Button>
                      )}
                    </div>

                    {showErasureForm && (
                      <div className="mt-3 space-y-3 bg-background border border-border rounded p-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-1">
                            Scope of deletion
                          </label>
                          <select
                            value={erasureScope}
                            onChange={(e) => setErasureScope(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                          >
                            <option value="all">All my personal data</option>
                            <option value="cv_profile">CV / skills profile only</option>
                            <option value="info_requests">Information requests only</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-1">
                            Reason (optional, helps the admin process faster)
                          </label>
                          <textarea
                            value={erasureReason}
                            onChange={(e) => setErasureReason(e.target.value)}
                            rows={2}
                            placeholder="e.g. I am leaving the company effective..."
                            className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="destructive" size="sm" onClick={handleSubmitErasure}>
                            Submit request
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setShowErasureForm(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Existing requests for this user */}
                    {erasureRequests.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-medium text-foreground">Your requests:</p>
                        {erasureRequests.map((r) => (
                          <div
                            key={r.id}
                            className="text-xs bg-background border border-border rounded p-2 flex items-center justify-between"
                          >
                            <div>
                              <span className={`inline-block px-2 py-0.5 rounded mr-2 ${
                                r.status === 'pending'   ? 'bg-warning/10 text-warning' :
                                r.status === 'approved'  ? 'bg-primary/10 text-primary' :
                                r.status === 'rejected'  ? 'bg-destructive/10 text-destructive' :
                                r.status === 'executed'  ? 'bg-success/10 text-success' :
                                'bg-muted text-muted-foreground'
                              }`}>{r.status}</span>
                              Submitted {new Date(r.submittedAt).toLocaleDateString('en-GB')}
                            </div>
                            {r.status === 'rejected' && r.rejectionReason && (
                              <span className="italic text-muted-foreground" title={r.rejectionReason}>
                                Why? Hover here.
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Data export, Art. 20 portability */}
                  <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-md">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{t('settings.privacy.exportData')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('settings.privacy.exportDataBody')}</p>
                    </div>
                    <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExportData}>
                      {t('settings.privacy.exportBtn')}
                    </Button>
                  </div>

                  {/* AI audit log */}
                  <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-md">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{t('settings.privacy.aiAuditLog')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('settings.privacy.aiAuditLogBody', { count: recentAuditCount })}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" iconName="Trash2" iconPosition="left" onClick={handleClearAudit} disabled={recentAuditCount === 0}>
                      {t('settings.privacy.clearAuditBtn')}
                    </Button>
                  </div>

                  {/* Profile data */}
                  {isEmployee && (
                    <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-md">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{t('settings.privacy.profileData')}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t('settings.privacy.profileDataBody')}</p>
                      </div>
                      <Button variant="ghost" size="sm" iconName="Trash2" iconPosition="left" onClick={handleClearProfile}>
                        {t('settings.privacy.clearProfileBtn')}
                      </Button>
                    </div>
                  )}

                  {/* Right to access, links to disclosure */}
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-md text-xs text-foreground">
                    <Icon name="Info" size={14} className="text-primary shrink-0 mt-0.5" />
                    <p>{t('settings.privacy.gdprNote')}</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
